import React, { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addDays, subDays } from 'date-fns';
export default function Calendar({
  getData,
  formatDate,
  pickedDate,
  setPickedDate,
}) {
  const isValidDate = (value) =>
    value instanceof Date && !Number.isNaN(value.getTime());

  const parsedPickedDate = useMemo(() => {
    const parsed = new Date(pickedDate);
    return isValidDate(parsed) ? parsed : new Date();
  }, [pickedDate]);

  // state variable that sets selectedDate to pickedDate
  const [selectedDate, setSelectedDate] = useState(parsedPickedDate);

  useEffect(() => {
    // Sets picked date when the component mounts
    if (!isValidDate(selectedDate)) {
      return;
    }
    setPickedDate(formatSelectedDate(selectedDate));
  }, [selectedDate, setPickedDate]);

  useEffect(() => {
    // Update the selectedDate when another date is selected
    setSelectedDate(parsedPickedDate);
  }, [parsedPickedDate]);

  useEffect(() => {
    if (getData && isValidDate(selectedDate)) {
      getData(formatSelectedDate(selectedDate));
    }
  }, [selectedDate, getData]);

  // formatting the date
  const formatSelectedDate = (date) => {
    return formatDate(date);
  };

  const dateForward = () => {
    setSelectedDate(addDays(selectedDate, 1))
  }

  const dateBackward = () => {
    setSelectedDate(subDays(selectedDate, 1))
  }


  const handleDateChange = (date) => {
    if (!isValidDate(date)) {
      return;
    }
    setSelectedDate(date);
  };

  const makeTodayButtonAccessible = () => {
    const todayButton = document.querySelector(".react-datepicker__today-button");
    if (!(todayButton instanceof HTMLElement)) {
      return;
    }

    todayButton.setAttribute("tabindex", "0");
    todayButton.setAttribute("role", "button");
    todayButton.setAttribute("aria-label", "Jump to today");

    if (todayButton.dataset.kxKeyHandlerBound === "true") {
      return;
    }

    todayButton.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        todayButton.click();
      }
    });
    todayButton.dataset.kxKeyHandlerBound = "true";
  };

  // month and year dropdown is commented out for future usage if needed.
  return (
    <div className="flex w-fit shadow-knowtex-shadow rounded-xl border-0" data-testid="datepicker">
      <label
        id="dashboard-calendar-date-label"
        htmlFor="dashboard-calendar-date-input"
        className="sr-only"
      >
        Appointment date
      </label>
      <p id="dashboard-calendar-date-help" className="sr-only">
        Use the previous and next date buttons, or open the calendar to select an
        appointment date.
      </p>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        Selected appointment date {formatSelectedDate(selectedDate)}
      </p>
      <button
        type="button"
        aria-label="Previous date"
        className="block min-w-[20px] text-xl font-bold text-accent1 hover:text-purple2 mx-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent1 rounded"
        onClick={dateBackward}
      >
        <svg className="w-6 h-6 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m15 19-7-7 7-7" />
        </svg>
      </button>
      <DatePicker
        id="dashboard-calendar-date-input"
        ariaLabelledBy="dashboard-calendar-date-label"
        ariaDescribedBy="dashboard-calendar-date-help"
        selected={selectedDate}
        onChange={handleDateChange}
        onCalendarOpen={() => {
          // DatePicker renders the Today element after opening; defer one tick.
          setTimeout(makeTodayButtonAccessible, 0);
        }}
        dateFormat="EEEE, MMMM d, yyyy"
        // showMonthDropdown
        // showYearDropdown
        dropdownMode="select"
        showIcon
        toggleCalendarOnIconClick
        todayButton="Today"
        className="rounded-md text-2xl border-none text-accent1"
      />
      <button
        type="button"
        aria-label="Next date"
        className="block min-w-[20px] text-xl text-accent1 hover:text-purple2 mx-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent1 rounded"
        onClick={dateForward}
      >
        <svg className="w-6 h-6 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m9 5 7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}
